import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SelectItem } from 'primeng/api';
import { BaseDataService } from './base.data.service';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import {
  IOrderBookingSummary,
  IOrderBookingHealthCheckDTO,
} from '../interfaces/order-booking-health-check/order-booking-health-check';
import { IOrderBookingHealthCheckAssigneeCounts } from '../interfaces/order-booking-health-check/order-booking-health-check';
import { IOrderBookingSearch } from '../interfaces/order-booking-health-check/order-booking-search';
import { IOrderBookingHealthCheckFilterDto } from '../interfaces/order-booking-health-check/order-booking-health-check-filter-dto';
import { IOrderBookingCheckConfiguration } from '../interfaces/order-booking-health-check/order-booking-check-configuration';
import { IOrderBookingCheckInfo } from '../interfaces/order-booking-health-check/order-booking-check-info';

@Injectable({
  providedIn: 'root',
})
export class OrderBookingHealthCheckService extends BaseDataService {
  public url = '/api/order-booking-health-check';

  constructor(public http: HttpClient, handlersFactory: HttpResponsesHandlersFactory) {
    super(http, handlersFactory);
  }

  public get(request: IOrderBookingSearch): Promise<IOrderBookingHealthCheckDTO> {
    const params = this.constructHttpParams(request);

    return this.http.get<IOrderBookingHealthCheckDTO>(this.url, { params }).toPromise();
  }

  public getFilterOptions() {
    return this.http
      .get<IOrderBookingHealthCheckFilterDto>(this.url + '/filter-options')
      .toPromise();
  }

  public getChecks() {
    return this.http.get<IOrderBookingCheckInfo[]>(this.url + '/checks').toPromise();
  }

  public getAssignees() {
    return this.http.get<SelectItem[]>(this.url + '/assignees').toPromise();
  }

  public assignUser(orderId: number, assigneeId: string) {
    return this.http
      .put(this.url + `/assign?orderId=${orderId}&assigneeId=${assigneeId ?? ''}`, {})
      .toPromise();
  }

  public unassignUser(orderId: number) {
    return this.http.put(this.url + `/unassign?orderId=${orderId}`, {}).toPromise();
  }

  public unassignAll() {
    return this.http.post(this.url + '/unassign/all', null).toPromise();
  }

  public unassignMe() {
    return this.http.post(this.url + '/unassign/me', null).toPromise();
  }

  public dismissIssue(issueId: number, reason: string) {
    return this.http.put(this.url + `/issues/${issueId}/dismiss`, { reason }).toPromise();
  }

  // Re-runs the checks for one booking and returns its refreshed state.
  public reevaluate(orderId: number): Promise<IOrderBookingSummary> {
    return this.http
      .post<IOrderBookingSummary>(this.url + `/evaluate/${orderId}`, null)
      .toPromise();
  }

  public getConfiguration() {
    return this.http
      .get<IOrderBookingCheckConfiguration[]>(this.url + '/configuration')
      .toPromise();
  }

  public upsertConfiguration(configuration: IOrderBookingCheckConfiguration) {
    return this.http
      .post<IOrderBookingCheckConfiguration>(this.url + '/configuration', configuration)
      .toPromise();
  }

  public deleteConfiguration(id: number) {
    return this.http.delete(this.url + `/configuration/${id}`).toPromise();
  }
}
